import React, { useCallback, useEffect, useState } from "react";
import { useParams, useLocation, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { API_URL, pathVideoCall } from "../../config/constants";
import socket from "../../socket";
import AppSidebar from "../../components/AppSidebar";
import CaregiverChat from "../../components/caregiverChat";
import VideoConsultationPanel from "../../components/videoConsultationPanel";
import ScheduleConsultationModal from "../../components/scheduleConsultationModal";
import JoinConsultationModal from "../../components/joinConsultationModal";
import EditPatientMessageModal from "../../components/editPatientMessageModal";
import avatarMap from "../../assets/avatars";
import therapistAvatar from "../../assets/therapist.png";
import {
  Layout, Main, TopBar, Content, PageTitle, Columns, ChatColumn,
  DetailsCard, DetailsTitle, PatientRow, PatientAvatar, PatientCode, DetailLine,
  SideColumn, SectionTitle, ScheduleButton,
  MessageLabel, MessageBox, MessageEmpty, EditMessageRow, EditMessageButton,
} from "./styles";

const IconCalendar = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const CaregiverSupport = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const location = useLocation();
  const history = useHistory();
  const token = localStorage.getItem("token");
  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");

  const [patient, setPatient] = useState(location.state?.patient || null);
  const [sessions, setSessions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [joinTarget, setJoinTarget] = useState(null);
  const [showMessage, setShowMessage] = useState(false);

  // Abrir el chat marca como leídos los mensajes de ese paciente, que es lo
  // que apaga la badge roja del listado.
  const markRead = useCallback(() => {
    fetch(`${API_URL}/messages/${id}/read`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => {});
  }, [id, token]);

  const loadPatient = useCallback(() => {
    fetch(`${API_URL}/patients/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => { if (!data.error) setPatient(data); })
      .catch(() => {});
  }, [id, token]);

  const loadSessions = useCallback(() => {
    fetch(`${API_URL}/sessions/upcoming?with=${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => setSessions(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [id, token]);

  useEffect(() => {
    loadPatient();
    loadSessions();
    markRead();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Si llega un mensaje con el chat abierto, ya está leído.
  useEffect(() => {
    const onMessage = (msg) => { if (msg.sender_id === Number(id)) markRead(); };
    socket.on("receiveMessage", onMessage);
    return () => socket.off("receiveMessage", onMessage);
  }, [id, markRead]);

  // El paciente acepta/rechaza desde su pantalla: refrescamos al vuelo.
  useEffect(() => {
    const onSessionUpdated = () => loadSessions();
    socket.on("sessionUpdated", onSessionUpdated);
    return () => socket.off("sessionUpdated", onSessionUpdated);
  }, [loadSessions]);

  const cancelConsultation = async (sessionId) => {
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/sessions/${sessionId}/cancel`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      toast.success(t("consultation.toastCancelled"));
      loadSessions();
    } catch {
      toast.error(t("consultation.toastError"));
    } finally {
      setBusy(false);
    }
  };

  const joinCall = (session) => {
    setJoinTarget(null);
    history.push(pathVideoCall, {
      patient: { id: Number(id), label: patient?.patient_code || "" },
      sessionId: session.id,
    });
  };

  // El chat está restringido al paciente asignado; el servidor lo rechaza
  // igualmente, esto evita enseñar una pantalla que no va a funcionar.
  const isMine = !patient || patient.assigned_therapist_id === currentUser.id;

  if (!isMine) {
    return (
      <Layout>
        <AppSidebar patient={patient} activeNav="caregiverSupport" />
        <Main>
          <TopBar />
          <Content>
            <PageTitle>{t("caregiver.title")}</PageTitle>
            <p style={{ color: "#55606b", fontSize: "0.9rem" }}>{t("caregiver.notAssigned")}</p>
          </Content>
        </Main>
      </Layout>
    );
  }

  return (
    <Layout>
      <AppSidebar patient={patient} activeNav="caregiverSupport" />

      <Main>
        <TopBar />

        <Content>
          <PageTitle>{t("caregiver.title")}</PageTitle>

          <Columns>
            <ChatColumn>
              <CaregiverChat
                title={t("caregiver.chatWith", { code: patient?.patient_code || "" })}
                currentUserId={currentUser.id}
                otherUserId={Number(id)}
                otherAvatar={avatarMap[patient?.avatar_url] || Object.values(avatarMap)[0]}
                ownAvatar={therapistAvatar}
              />
            </ChatColumn>

            <SideColumn>
              <DetailsCard>
                <DetailsTitle>{t("caregiver.patientDetails")}</DetailsTitle>

                <PatientRow>
                  <PatientAvatar
                    src={avatarMap[patient?.avatar_url] || Object.values(avatarMap)[0]}
                    alt="avatar"
                  />
                  <PatientCode>{patient?.patient_code || "—"}</PatientCode>
                </PatientRow>

                <DetailLine>
                  <strong>{t("patients.table.cohort")}:</strong> {patient?.cohort ?? "—"}
                </DetailLine>
                <DetailLine>
                  <strong>{t("patients.table.age")}:</strong> {patient?.age_at_start_corrected || "—"}
                </DetailLine>
                <DetailLine>
                  <strong>{t("patients.table.sex")}:</strong> {patient?.sex || "—"}
                </DetailLine>
                <DetailLine>
                  <strong>{t("patients.table.pathology")}:</strong> {patient?.pathology || "—"}
                </DetailLine>

                <MessageLabel>{t("patientMessage.label")}</MessageLabel>
                {patient?.therapist_message ? (
                  <MessageBox>{patient.therapist_message}</MessageBox>
                ) : (
                  <MessageEmpty>{t("patientMessage.empty")}</MessageEmpty>
                )}

                <EditMessageRow>
                  <EditMessageButton onClick={() => setShowMessage(true)}>
                    {t("patientMessage.edit")}
                  </EditMessageButton>
                </EditMessageRow>
              </DetailsCard>

              <div>
                <SectionTitle>{t("caregiver.videoConsultation")}</SectionTitle>
                <ScheduleButton onClick={() => setShowSchedule(true)}>
                  <IconCalendar />
                  {t("caregiver.schedule")}
                </ScheduleButton>
              </div>

              <VideoConsultationPanel
                sessions={sessions}
                busy={busy}
                fill
                onJoin={setJoinTarget}
                onCancel={cancelConsultation}
              />
            </SideColumn>
          </Columns>
        </Content>
      </Main>

      {showSchedule && (
        <ScheduleConsultationModal
          patientId={Number(id)}
          onClose={() => setShowSchedule(false)}
          onScheduled={() => {
            setShowSchedule(false);
            toast.success(t("scheduleConsultation.toastSent"));
            loadSessions();
          }}
        />
      )}

      {showMessage && (
        <EditPatientMessageModal
          patientId={Number(id)}
          message={patient?.therapist_message}
          onClose={() => setShowMessage(false)}
          onSaved={() => { setShowMessage(false); loadPatient(); }}
        />
      )}

      {joinTarget && (
        <JoinConsultationModal
          session={joinTarget}
          withLabel={patient?.patient_code || ""}
          roleLabel={t("consultation.readyModal.rolePatient")}
          onClose={() => setJoinTarget(null)}
          onJoin={() => joinCall(joinTarget)}
        />
      )}
    </Layout>
  );
};

export default CaregiverSupport;
